import { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  FaGithub,
  FaInstagram,
  FaLinkedin,
  FaEye,
  FaTimes,
  FaDownload,
  FaArrowRight,
  FaChevronDown,
  FaExternalLinkAlt,
  FaLaptopCode,
  FaServer,
  FaDatabase,
  FaGraduationCap,
  FaBriefcase,
  FaAward,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import {
  SiReact,
  SiFlutter,
  SiNodedotjs,
  SiFirebase,
  SiJavascript,
  SiPython,
  SiMysql,
  SiMongodb,
  SiLinux,
  SiBootstrap,
  SiGit,
  SiOpenjdk,
  SiDart,
} from 'react-icons/si';
import profilePic from '../assets/images/profile_pic.jpg';
import Contact from './Contact';
import Publications from './Publications';
import Footer from './Footer';
import ScrollReveal from './ScrollReveal';
import { projects } from '../data/projectsData';
import { publications } from '../data/publicationsData';

// --- STYLED COMPONENTS ---

const MainWrapper = styled.main`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-x: hidden;
  background-color: var(--bg-primary);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
  width: 100%;

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const SectionTag = styled.span`
  font-family: var(--font-mono);
  font-size: 0.875rem;
  color: var(--accent-cyan);
  background: rgba(6, 182, 212, 0.1);
  border: 1px solid rgba(6, 182, 212, 0.2);
  padding: 0.35rem 0.85rem;
  border-radius: 9999px;
  display: inline-block;
  margin-bottom: 0.85rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.35rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.5px;
  margin-bottom: 0.75rem;

  @media (max-width: 768px) {
    font-size: 1.85rem;
  }

  @media (max-width: 480px) {
    font-size: 1.55rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.05rem;
  color: var(--text-muted);
  max-width: 620px;
  margin: 0 auto 3rem;
  text-align: center;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3.5rem;
`;

// --- HERO SECTION ---
const HeroWrapper = styled.section`
  position: relative;
  min-height: calc(100vh - 80px);
  display: flex;
  align-items: center;
  padding: 5rem 0 4rem;
  background: var(--bg-primary);
  background-image: 
    radial-gradient(rgba(148, 163, 184, 0.08) 1px, transparent 1px),
    var(--gradient-hero);
  background-size: 28px 28px, 100% 100%;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 15%;
    left: -100px;
    width: 350px;
    height: 350px;
    background: radial-gradient(circle, rgba(6, 182, 212, 0.2) 0%, transparent 70%);
    filter: blur(60px);
    pointer-events: none;
  }

  &:after {
    content: '';
    position: absolute;
    bottom: 10%;
    right: -100px;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.18) 0%, transparent 70%);
    filter: blur(70px);
    pointer-events: none;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1.25fr 1fr;
  gap: 3.5rem;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 3rem;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 2;
  min-width: 0;
  width: 100%;

  @media (max-width: 968px) {
    align-items: center;
  }
`;

const Greeting = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--font-mono);
  font-size: 0.95rem;
  color: var(--accent-cyan);
  margin-bottom: 1rem;
  background: rgba(6, 182, 212, 0.1);
  border: 1px solid rgba(6, 182, 212, 0.2);
  padding: 0.35rem 0.9rem;
  border-radius: 9999px;
  width: fit-content;

  .wave {
    animation: waveHand 2.5s infinite;
    transform-origin: 70% 70%;
    display: inline-block;
  }

  @keyframes waveHand {
    0%, 60%, 100% { transform: rotate(0deg); }
    10%, 30% { transform: rotate(14deg); }
    20% { transform: rotate(-8deg); }
    40% { transform: rotate(14deg); }
    50% { transform: rotate(-4deg); }
  }
`;

const HeroName = styled.h1`
  font-size: clamp(2.1rem, 8.5vw, 3.6rem);
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.1;
  margin-bottom: 1rem;
  letter-spacing: -1px;
  overflow-wrap: break-word;

  span {
    background: var(--gradient-accent);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 480px) {
    font-size: clamp(1.9rem, 9vw, 2.4rem);
    letter-spacing: -0.5px;
  }
`;

const HeroRole = styled.div`
  font-size: clamp(1.2rem, 4.5vw, 1.65rem);
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 1.25rem;
  min-height: 2.2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 968px) {
    justify-content: center;
  }

  @media (max-width: 768px) {
    font-size: 1.35rem;
  }
`;

const HeroBio = styled.p`
  font-size: 1.05rem;
  color: var(--text-muted);
  line-height: 1.75;
  margin-bottom: 2rem;
  max-width: 560px;
  overflow-wrap: break-word;
  width: 100%;

  @media (max-width: 968px) {
    margin: 0 auto 2rem;
  }
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 968px) {
    justify-content: center;
  }
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 1.75rem;
  background: var(--btn-primary-bg, #2563eb);
  color: var(--btn-primary-text, #ffffff);
  border: 1px solid var(--btn-primary-border, rgba(255, 255, 255, 0.15));
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: var(--btn-primary-shadow, 0 1px 2px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(37, 99, 235, 0.28));

  &:hover {
    background: var(--btn-primary-hover, #1d4ed8);
    transform: translateY(-2px);
    box-shadow: var(--btn-primary-shadow-hover, 0 2px 4px rgba(0, 0, 0, 0.4), 0 6px 18px rgba(37, 99, 235, 0.38));
  }

  &:active {
    background: var(--btn-primary-active, #1e40af);
    transform: translateY(0);
  }
`;

const SecondaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 1.6rem;
  background: var(--bg-card);
  color: var(--text-primary);
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: var(--bg-tertiary);
    border-color: var(--btn-primary-bg, #2563eb);
    transform: translateY(-2px);
    color: var(--btn-primary-bg, #2563eb);
  }
`;

const SocialIconsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const HeroSocialIcon = styled.a`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  transition: all 0.25s ease;
  text-decoration: none;

  &:hover {
    background: var(--bg-tertiary);
    color: var(--accent-cyan);
    border-color: var(--accent-cyan);
    transform: translateY(-3px);
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  padding-top: 1.75rem;
  margin-top: 0.5rem;
  border-top: 1px solid var(--border-subtle);
  width: 100%;
  max-width: 560px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  .stat-value {
    font-size: 1.5rem;
    font-weight: 800;
    color: var(--text-primary);
    font-family: var(--font-mono);
  }

  .stat-label {
    font-size: 0.825rem;
    color: var(--text-dim);
    font-weight: 500;
  }
`;

// Hero Right Column: Photo + Floating Chips + Code Snippet
const HeroVisual = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  z-index: 2;
  min-width: 0;
  width: 100%;
`;

const PhotoCenterWrapper = styled.div`
  position: relative;
  width: 250px;
  height: 250px;
  margin: 0 auto;

  @media (max-width: 500px) {
    width: 200px;
    height: 200px;
  }
`;

const GlowingRing = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  padding: 6px;
  background: var(--gradient-accent);
  box-shadow: 0 0 35px rgba(6, 182, 212, 0.45);
  animation: pulseGlow 4s infinite alternate ease-in-out;

  @keyframes pulseGlow {
    0% {
      box-shadow: 0 0 25px rgba(6, 182, 212, 0.35);
    }
    100% {
      box-shadow: 0 0 45px rgba(139, 92, 246, 0.6);
    }
  }
`;

const ProfileImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid var(--bg-primary);
  display: block;
`;

const TechChip = styled.div`
  position: absolute;
  background: var(--bg-card-solid);
  border: 1px solid var(--border-subtle);
  color: var(--text-primary);
  padding: 0.45rem 0.85rem;
  border-radius: 12px;
  font-family: var(--font-mono);
  font-size: 0.775rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: var(--shadow-md);
  z-index: 5;
  animation: floatChip 5s infinite ease-in-out;
  animation-delay: ${props => props.$delay || '0s'};

  top: ${props => props.$top};
  left: ${props => props.$left};
  right: ${props => props.$right};
  bottom: ${props => props.$bottom};

  svg {
    font-size: 1.1rem;
    color: ${props => props.$color};
  }

  @keyframes floatChip {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-8px);
    }
  }

  @media (max-width: 500px) {
    display: none;
  }
`;

const CodeCard = styled.div`
  width: 100%;
  max-width: 440px;
  background: #0f172a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  font-family: var(--font-mono);
  font-size: 0.825rem;
  text-align: left;
`;

const CodeCardHeader = styled.div`
  background: #1e293b;
  padding: 0.65rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .dots {
    display: flex;
    gap: 6px;
    span {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      display: inline-block;
    }
    .red { background: #ef4444; }
    .yellow { background: #f59e0b; }
    .green { background: #10b981; }
  }

  .file-name {
    color: #94a3b8;
    font-size: 0.75rem;
    margin-left: 0.5rem;
  }
`;

const CodeCardBody = styled.pre`
  padding: 1.15rem;
  color: #e2e8f0;
  line-height: 1.6;
  margin: 0;
  overflow-x: auto;

  .keyword { color: #f472b6; }
  .variable { color: #38bdf8; }
  .property { color: #a78bfa; }
  .string { color: #34d399; }
  .bracket { color: #fbbf24; }
`;

const ScrollDownIndicator = styled.a`
  position: absolute;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  color: var(--text-dim);
  text-decoration: none;
  font-family: var(--font-mono);
  font-size: 0.75rem;
  transition: color 0.2s;
  animation: bounceDown 2s infinite;

  &:hover {
    color: var(--accent-cyan);
  }

  @keyframes bounceDown {
    0%, 20%, 50%, 80%, 100% {
      transform: translate(-50%, 0);
    }
    40% {
      transform: translate(-50%, -6px);
    }
    60% {
      transform: translate(-50%, -3px);
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

// --- ABOUT STRIP (NEW) ---
const AboutSection = styled.section`
  padding: 5rem 0;
  background-color: var(--bg-secondary);
  border-top: 1px solid var(--border-subtle);
  border-bottom: 1px solid var(--border-subtle);
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1.35fr 1fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutTextCol = styled.div`
  h2 {
    font-size: 2rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 1.25rem;
  }

  p {
    color: var(--text-muted);
    font-size: 1.025rem;
    line-height: 1.8;
    margin-bottom: 1.25rem;
  }
`;

const FactsCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  h3 {
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 0.25rem;
  }
`;

const GalleryGrid = styled.div`
  margin-top: 1.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1.25rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  }
`;

const GalleryPhoto = styled.div`
  width: 100%;
  padding-top: 75%;
  border-radius: 12px;
  background-color: var(--bg-tertiary);
  background-size: cover;
  background-position: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.03);
    box-shadow: var(--shadow-md);
  }
`;

const GalleryModalImage = styled.img`
  max-width: 92vw;
  max-height: 88vh;
  object-fit: contain;
  border-radius: 10px;
`;

const FactItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  border-bottom: 1px solid var(--border-subtle);
  padding-bottom: 0.85rem;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .label {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--accent-cyan);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .value {
    color: var(--text-primary);
    font-size: 0.95rem;
    font-weight: 500;
  }
`;

// --- WHAT I DO SECTION ---
const WhatIDoSection = styled.section`
  padding: 5rem 0;
  background-color: var(--bg-primary);
`;

const WhatIDoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 16px;
  padding: 2.25rem;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--gradient-accent);
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover {
    transform: translateY(-6px);
    border-color: var(--border-hover);
    box-shadow: var(--shadow-md), var(--shadow-glow);

    &:before {
      opacity: 1;
    }
  }

  .icon-box {
    width: 52px;
    height: 52px;
    border-radius: 12px;
    background: rgba(6, 182, 212, 0.12);
    border: 1px solid rgba(6, 182, 212, 0.2);
    color: var(--accent-cyan);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
  }

  h3 {
    font-size: 1.35rem;
    font-weight: 700;
    color: var(--text-primary);
  }

  p {
    font-size: 0.95rem;
    color: var(--text-muted);
    line-height: 1.65;
  }
`;

// --- SKILLS SECTION ---
const SkillsSection = styled.section`
  padding: 5rem 0;
  background-color: var(--bg-secondary);
  border-top: 1px solid var(--border-subtle);
`;

const FilterTabs = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-bottom: 2.5rem;
`;

const FilterTab = styled.button`
  font-family: var(--font-mono);
  font-size: 0.85rem;
  padding: 0.5rem 1.15rem;
  border-radius: 9999px;
  border: 1px solid ${props => (props.$active ? 'var(--accent-cyan)' : 'var(--border-subtle)')};
  background: ${props => (props.$active ? 'rgba(6, 182, 212, 0.15)' : 'var(--bg-card)')};
  color: ${props => (props.$active ? 'var(--accent-cyan)' : 'var(--text-secondary)')};
  font-weight: ${props => (props.$active ? '600' : '500')};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--accent-cyan);
    color: var(--text-primary);
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 1.25rem;

  @media (max-width: 500px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SkillCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 14px;
  padding: 1.35rem 1rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.25s ease;
  box-shadow: var(--shadow-sm);

  &:hover {
    transform: translateY(-4px);
    border-color: var(--border-hover);
    box-shadow: var(--shadow-md);

    svg {
      transform: scale(1.1);
    }
  }

  svg {
    font-size: 2.2rem;
    transition: transform 0.2s;
  }

  span {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-primary);
  }
`;

// --- PROJECTS SECTION ---
const ProjectsSection = styled.section`
  padding: 6rem 0;
  background-color: var(--bg-primary);
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2.5rem;

  @media (max-width: 868px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.article`
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;

  &:hover {
    transform: translateY(-8px);
    border-color: var(--border-hover);
    box-shadow: var(--shadow-lg), var(--shadow-glow);

    .img-zoom {
      transform: scale(1.06);
    }
  }
`;

const ProjectImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 240px;
  overflow: hidden;
  background: var(--bg-tertiary);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.4s ease;
  }
`;

const ProjectBody = styled.div`
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;

  h3 {
    font-size: 1.35rem;
    font-weight: 700;
    color: var(--text-primary);
  }

  p {
    font-size: 0.95rem;
    color: var(--text-muted);
    line-height: 1.6;
  }
`;

const TechTagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const TechTag = styled.span`
  font-family: var(--font-mono);
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.65rem;
  background: var(--bg-tertiary);
  color: var(--accent-cyan);
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
`;

const ProjectButtonsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: auto;
  padding-top: 0.75rem;
`;

const ProjectBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.6rem 1.15rem;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.2s ease;
  background: ${props => (props.$primary ? 'var(--btn-primary-bg, #2563eb)' : 'var(--bg-secondary)')};
  color: ${props => (props.$primary ? 'var(--btn-primary-text, #ffffff)' : 'var(--text-primary)')};
  border: 1px solid ${props => (props.$primary ? 'var(--btn-primary-border, rgba(255, 255, 255, 0.12))' : 'var(--border-subtle)')};
  box-shadow: ${props => (props.$primary ? 'var(--btn-primary-shadow, 0 1px 2px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(37, 99, 235, 0.2))' : 'none')};

  &:hover {
    transform: translateY(-2px);
    background: ${props => (props.$primary ? 'var(--btn-primary-hover, #1d4ed8)' : 'var(--bg-tertiary)')};
    color: ${props => (props.$primary ? '#ffffff' : 'var(--btn-primary-bg, #2563eb)')};
    border-color: ${props => (props.$primary ? 'transparent' : 'var(--btn-primary-bg, #2563eb)')};
    box-shadow: ${props => (props.$primary ? 'var(--btn-primary-shadow-hover, 0 4px 12px rgba(37, 99, 235, 0.3))' : 'var(--shadow-sm)')};
  }

  &:active {
    background: ${props => (props.$primary ? 'var(--btn-primary-active, #1e40af)' : 'var(--bg-tertiary)')};
    transform: translateY(0);
  }
`;

const ViewMoreLink = styled(Link)`
  font-size: 0.85rem;
  color: var(--accent-cyan);
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  text-decoration: none;
  margin-left: auto;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const AllProjectsBtnWrapper = styled.div`
  text-align: center;
  margin-top: 3.5rem;
`;

const ViewAllProjectsBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 2rem;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  color: var(--text-primary);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.25s ease;

  &:hover {
    background: var(--bg-tertiary);
    border-color: var(--accent-cyan);
    color: var(--accent-cyan);
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
  }
`;

// --- EXPERIENCE & EDUCATION TIMELINE (NEW) ---
const TimelineSection = styled.section`
  padding: 6rem 0;
  background-color: var(--bg-secondary);
  border-top: 1px solid var(--border-subtle);
`;

const TimelineWrapper = styled.div`
  position: relative;
  max-width: 900px;
  margin: 0 auto;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    background: var(--border-subtle);
    transform: translateX(-50%);

    @media (max-width: 768px) {
      left: 20px;
    }
  }
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: 3rem;
  display: flex;
  justify-content: ${props => (props.$alignRight ? 'flex-end' : 'flex-start')};

  @media (max-width: 768px) {
    justify-content: flex-start;
    padding-left: 50px;
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--bg-card-solid);
  border: 2px solid var(--accent-cyan);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent-cyan);
  font-size: 0.95rem;
  z-index: 2;
  box-shadow: 0 0 12px rgba(6, 182, 212, 0.4);

  @media (max-width: 768px) {
    left: 20px;
  }
`;

const TimelineCard = styled.div`
  width: 44%;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 14px;
  padding: 1.75rem;
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: var(--border-hover);
    box-shadow: var(--shadow-md);
  }

  .date {
    font-family: var(--font-mono);
    font-size: 0.8rem;
    color: var(--accent-cyan);
    margin-bottom: 0.4rem;
  }

  h3 {
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 0.35rem;
  }

  h4 {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--text-secondary);
    margin-bottom: 0.75rem;
  }

  p {
    font-size: 0.875rem;
    color: var(--text-muted);
    line-height: 1.6;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

// --- GITHUB STATS SECTION (NEW) ---
const GitHubSection = styled.section`
  padding: 5rem 0;
  background-color: var(--bg-primary);
  border-top: 1px solid var(--border-subtle);
`;

const GitHubGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(320px, 100%), 1fr));
  gap: 2rem;
  align-items: center;
  justify-content: center;
`;

const GitHubCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: var(--shadow-sm);
  overflow: hidden;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
  }
`;

const GitHubCtaWrapper = styled.div`
  text-align: center;
  margin-top: 2.5rem;
`;

const GitHubCtaBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 1.85rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  color: var(--text-primary);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.25s ease;

  &:hover {
    background: var(--bg-tertiary);
    border-color: var(--accent-cyan);
    color: var(--accent-cyan);
    transform: translateY(-2px);
  }
`;

// --- CALL TO ACTION BAND (NEW) ---
const CtaBand = styled.section`
  padding: 5rem 1.5rem;
  background: linear-gradient(135deg, #0b1e36 0%, #0c335a 50%, #201844 100%);
  color: #ffffff;
  text-align: center;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(6, 182, 212, 0.2) 0%, transparent 60%);
    pointer-events: none;
  }
`;

const CtaTitle = styled.h2`
  font-size: 2.75rem;
  font-weight: 800;
  letter-spacing: -0.5px;
  margin-bottom: 1rem;
  color: #ffffff;

  @media (max-width: 768px) {
    font-size: 2.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1.7rem;
  }
`;

const CtaSubtitle = styled.p`
  font-size: 1.15rem;
  color: #cbd5e1;
  max-width: 580px;
  margin: 0 auto 2.5rem;
  line-height: 1.7;
`;

const CtaBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1rem 2.25rem;
  background: var(--btn-primary-bg, #2563eb);
  color: var(--btn-primary-text, #ffffff);
  text-decoration: none;
  border: 1px solid var(--btn-primary-border, rgba(255, 255, 255, 0.15));
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.05rem;
  transition: all 0.25s ease;
  box-shadow: var(--btn-primary-shadow, 0 2px 4px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(37, 99, 235, 0.3));

  &:hover {
    background: var(--btn-primary-hover, #1d4ed8);
    transform: translateY(-2px);
    box-shadow: var(--btn-primary-shadow-hover, 0 4px 8px rgba(0, 0, 0, 0.2), 0 8px 24px rgba(37, 99, 235, 0.4));
    color: #ffffff;
  }

  &:active {
    background: var(--btn-primary-active, #1e40af);
    transform: translateY(0);
  }
`;

// --- RESUME MODAL ---
const ResumeModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1.5rem;
  animation: fadeIn 0.25s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ResumeModalContent = styled.div`
  background: var(--bg-card-solid);
  border-radius: 14px;
  width: 100%;
  max-width: 950px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-subtle);

  @media (max-width: 768px) {
    height: 94vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background: #1e293b;
  color: #ffffff;

  h3 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 600;
    color: #f8fafc;
  }
`;

const ModalActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ModalActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  text-decoration: none;
  font-size: 0.85rem;
  padding: 0.45rem 0.9rem;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #0284c7;
    color: #ffffff;
  }
`;

const CloseModalButton = styled.button`
  background: transparent;
  border: none;
  color: #cbd5e1;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0.4rem;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const ResumeIframe = styled.iframe`
  flex: 1;
  width: 100%;
  border: none;
  background: #525659;
`;

// --- COMPONENT FUNCTION ---
export default function Home() {
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);

  const aboutImages = [
    '/assets/about-images/1.jpg',
    '/assets/about-images/2.jpg',
    '/assets/about-images/3.jpg',
    '/assets/about-images/4.jpg',
    '/assets/about-images/5.jpg',
    '/assets/about-images/6.jpg',
    '/assets/about-images/7.jpg',
    '/assets/about-images/8.png',
    '/assets/about-images/9.jpg',
    '/assets/about-images/10.jpg',
    '/assets/about-images/11.jpg',
    '/assets/about-images/12.jpg',
  ];

  // Skill items dataset
  const allSkills = [
    { name: 'Python', category: 'Backend', icon: <SiPython color="#3776ab" /> },
    { name: 'SQL', category: 'Database', icon: <SiMysql color="#00758f" /> },
    { name: 'Dart', category: 'Mobile', icon: <SiDart color="#0175c2" /> },
    { name: 'Java', category: 'Backend', icon: <SiOpenjdk color="#e76f00" /> },
    { name: 'JavaScript', category: 'Frontend', icon: <SiJavascript color="#f7df1e" /> },
    { name: 'Flutter', category: 'Mobile', icon: <SiFlutter color="#02569b" /> },
    { name: 'React.js', category: 'Frontend', icon: <SiReact color="#61dafb" /> },
    { name: 'Node.js', category: 'Backend', icon: <SiNodedotjs color="#339933" /> },
    { name: 'MongoDB', category: 'Database', icon: <SiMongodb color="#47a248" /> },
    { name: 'MySQL', category: 'Database', icon: <SiMysql color="#4479a1" /> },
    { name: 'Firebase', category: 'Backend', icon: <SiFirebase color="#ffca28" /> },
    { name: 'Linux', category: 'Tools', icon: <SiLinux color="#fcc624" /> },
    { name: 'Bootstrap', category: 'Frontend', icon: <SiBootstrap color="#7952b3" /> },
    { name: 'Git & GitHub', category: 'Tools', icon: <SiGit color="#f05032" /> },
  ];

  const categories = ['All', 'Frontend', 'Backend', 'Mobile', 'Database', 'Tools'];

  const filteredSkills =
    activeCategory === 'All'
      ? allSkills
      : allSkills.filter((s) => s.category === activeCategory);

  const displayProjects = projects;

  return (
    <MainWrapper>
      {/* 1. HERO SECTION */}
      <HeroWrapper id="home">
        <Container>
          <HeroGrid>
            <HeroContent>
              <Greeting>
                <span className="wave">👋</span> Hello, I'm
              </Greeting>

              <HeroName>
                Nimish <span>Kushwaha</span>
              </HeroName>

              <HeroRole>
                Full Stack Developer
              </HeroRole>

              <HeroBio>
                Enthusiastic and passionate about building visually appealing, responsive, and user-friendly web and mobile applications. Proficient in React, Flutter, and modern web development technologies, eager to translate ideas into seamless digital experiences.
              </HeroBio>

              <HeroActions>
                <PrimaryBtn type="button" onClick={() => setIsResumeModalOpen(true)}>
                  <FaEye /> View Resume
                </PrimaryBtn>
                <SecondaryBtn href="#contact">Contact Me</SecondaryBtn>

                <SocialIconsBar>
                  <HeroSocialIcon
                    href="https://github.com/NimishKushwaha"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="GitHub"
                    aria-label="GitHub Profile"
                  >
                    <FaGithub />
                  </HeroSocialIcon>
                  <HeroSocialIcon
                    href="https://www.linkedin.com/in/nimishkushwaha01"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="LinkedIn"
                    aria-label="LinkedIn Profile"
                  >
                    <FaLinkedin />
                  </HeroSocialIcon>
                  <HeroSocialIcon
                    href="http://instagram.com/nimishkushwaha01"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Instagram"
                    aria-label="Instagram Profile"
                  >
                    <FaInstagram />
                  </HeroSocialIcon>
                </SocialIconsBar>
              </HeroActions>

              <StatsRow>
                <StatItem>
                  <span className="stat-value">2</span>
                  <span className="stat-label">Publications</span>
                </StatItem>
                <StatItem>
                  <span className="stat-value">5+</span>
                  <span className="stat-label">Projects Built</span>
                </StatItem>
                <StatItem>
                  <span className="stat-value">IEEE</span>
                  <span className="stat-label">Conference Author</span>
                </StatItem>
              </StatsRow>
            </HeroContent>

            <HeroVisual>
              <PhotoCenterWrapper>
                <GlowingRing>
                  <ProfileImg src={profilePic} alt="Nimish Kushwaha" />
                </GlowingRing>

                {/* Floating Tech Chips around photo */}
                <TechChip $top="-15px" $left="-25px" $color="#61dafb" $delay="0s">
                  <SiReact /> React.js
                </TechChip>
                <TechChip $top="-15px" $right="-25px" $color="#02569b" $delay="1.2s">
                  <SiFlutter /> Flutter
                </TechChip>
                <TechChip $bottom="-15px" $left="-20px" $color="#339933" $delay="2.4s">
                  <SiNodedotjs /> Node.js
                </TechChip>
                <TechChip $bottom="-15px" $right="-20px" $color="#47a248" $delay="3.6s">
                  <FaDatabase /> Database
                </TechChip>
              </PhotoCenterWrapper>

              {/* Code Snippet Card */}
              <CodeCard>
                <CodeCardHeader>
                  <div className="dots">
                    <span className="red" />
                    <span className="yellow" />
                    <span className="green" />
                  </div>
                  <span className="file-name">developer.config.ts</span>
                </CodeCardHeader>
                <CodeCardBody>
                  <code>
                    <span className="keyword">const</span> <span className="variable">developer</span> = <span className="bracket">{'{'}</span>{'\n'}
                    {'  '}<span className="property">name</span>: <span className="string">"Nimish Kushwaha"</span>,{'\n'}
                    {'  '}<span className="property">location</span>: <span className="string">"Kathmandu, Nepal"</span>,{'\n'}
                    {'  '}<span className="property">stack</span>: [<span className="string">"React"</span>, <span className="string">"Flutter"</span>, <span className="string">"Node.js"</span>],{'\n'}
                    {'  '}<span className="property">focus</span>: <span className="string">"Federated Learning, Web &amp; Mobile App"</span>,{'\n'}
                    {'  '}<span className="property">availableForHire</span>: <span className="keyword">true</span>{'\n'}
                    <span className="bracket">{'}'}</span>;
                  </code>
                </CodeCardBody>
              </CodeCard>
            </HeroVisual>
          </HeroGrid>
        </Container>

        <ScrollDownIndicator href="#about" aria-label="Scroll down to About section">
          <span>SCROLL</span>
          <FaChevronDown />
        </ScrollDownIndicator>
      </HeroWrapper>

      {/* 2. ABOUT STRIP (NEW) */}
      <ScrollReveal id="about">
        <AboutSection>
          <Container>
            <AboutGrid>
              <AboutTextCol>
                <SectionTag>01. // about</SectionTag>
                <h2>Passionate Developer &amp; AI Researcher</h2>
                <p>
                  Hello! I am Nimish Kushwaha, a Computer Science and Engineering student at Amrita Vishwa Vidyapeetham, Chennai. I thrive on translating ambitious ideas into responsive web and mobile applications that prioritize speed, accessibility, and intuitive user experiences.
                </p>
                <p>
                  Beyond software engineering, my research explores collaborative, privacy-preserving Federated Learning in healthcare—jointly training deep neural networks for medical imaging across decentralized hospitals without compromising confidential patient records.
                </p>
              </AboutTextCol>

              <FactsCard>
                <h3>Quick Facts</h3>
                <FactItem>
                  <span className="label">Location</span>
                  <span className="value">
                    <FaMapMarkerAlt style={{ marginRight: '6px', color: 'var(--accent-cyan)' }} />
                    Sitapaila, Nagarjun, Kathmandu, Nepal
                  </span>
                </FactItem>
                <FactItem>
                  <span className="label">Focus Areas</span>
                  <span className="value">Full Stack Web, Flutter Mobile, Federated Learning</span>
                </FactItem>
                <FactItem>
                  <span className="label">Currently Learning</span>
                  <span className="value">Advanced Differential Privacy &amp; Scalable Cloud Arch</span>
                </FactItem>
                <FactItem>
                  <span className="label">Education</span>
                  <span className="value">B.Tech in Computer Science &amp; Engineering</span>
                </FactItem>
              </FactsCard>
            </AboutGrid>

            <h3 style={{ marginTop: '3rem', fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center' }}>Gallery</h3>
            <GalleryGrid>
              {aboutImages.map((image, index) => (
                <GalleryPhoto
                  key={index}
                  style={{ backgroundImage: `url(${image})` }}
                  onClick={() => setSelectedImage(image)}
                  role="button"
                  aria-label={`Gallery image ${index + 1}`}
                />
              ))}
            </GalleryGrid>
          </Container>
        </AboutSection>
      </ScrollReveal>

      {/* 3. WHAT I DO SECTION */}
      <ScrollReveal>
        <WhatIDoSection>
          <Container>
            <SectionHeader>
              <SectionTag>capabilities</SectionTag>
              <SectionTitle>What I Do</SectionTitle>
              <SectionSubtitle>
                Specialized in architecting high-performance client experiences, resilient backends, and robust schemas.
              </SectionSubtitle>
            </SectionHeader>

            <WhatIDoGrid>
              <ServiceCard>
                <div className="icon-box">
                  <FaLaptopCode />
                </div>
                <h3>Frontend Development</h3>
                <p>
                  Crafting highly responsive, aesthetic, and accessible user interfaces using React.js, modern CSS/styled-components, and Flutter. Delivering seamless animations, cross-device parity, and sub-second load times.
                </p>
              </ServiceCard>

              <ServiceCard>
                <div className="icon-box">
                  <FaServer />
                </div>
                <h3>Backend Development</h3>
                <p>
                  Building robust, scalable server-side systems and RESTful APIs using Node.js, Express, and Python. Ensuring secure authentication, optimized microservices, and reliable integration pipelines.
                </p>
              </ServiceCard>

              <ServiceCard>
                <div className="icon-box">
                  <FaDatabase />
                </div>
                <h3>Database Design</h3>
                <p>
                  Architecting efficient relational and document databases with MongoDB and MySQL. Focused on schema normalization, index optimization, data integrity, and low-latency queries for complex workloads.
                </p>
              </ServiceCard>
            </WhatIDoGrid>
          </Container>
        </WhatIDoSection>
      </ScrollReveal>

      {/* 4. SKILLS SECTION (MERGED & FILTERABLE) */}
      <ScrollReveal id="skills">
        <SkillsSection>
          <Container>
            <SectionHeader>
              <SectionTag>02. // skills</SectionTag>
              <SectionTitle>Technical Expertise</SectionTitle>
              <SectionSubtitle>
                Languages, libraries, frameworks, and developer tools I leverage to build end-to-end applications.
              </SectionSubtitle>
            </SectionHeader>

            <FilterTabs>
              {categories.map((cat) => (
                <FilterTab
                  key={cat}
                  $active={activeCategory === cat}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </FilterTab>
              ))}
            </FilterTabs>

            <SkillsGrid>
              {filteredSkills.map((skill) => (
                <SkillCard key={skill.name}>
                  {skill.icon}
                  <span>{skill.name}</span>
                </SkillCard>
              ))}
            </SkillsGrid>
          </Container>
        </SkillsSection>
      </ScrollReveal>

      {/* 5. PROJECTS SECTION */}
      <ScrollReveal id="projects">
        <ProjectsSection>
          <Container>
            <SectionHeader>
              <SectionTag>03. // projects</SectionTag>
              <SectionTitle>Featured Projects</SectionTitle>
              <SectionSubtitle>
                A showcase of web applications, mobile platforms, and research prototypes built with modern technologies.
              </SectionSubtitle>
            </SectionHeader>

            <ProjectsGrid>
              {displayProjects.map((project) => (
                <ProjectCard key={project.id}>
                  <ProjectImageWrapper>
                    <img className="img-zoom" src={project.thumbnail} alt={project.title} />
                  </ProjectImageWrapper>

                  <ProjectBody>
                    <h3>{project.title}</h3>
                    <TechTagsRow>
                      {project.technologies.map((tech) => (
                        <TechTag key={tech}>{tech}</TechTag>
                      ))}
                    </TechTagsRow>
                    <p>{project.description}</p>

                    <ProjectButtonsRow>
                      {project.projectLink && (
                        <ProjectBtn
                          href={project.projectLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          $primary
                        >
                          <FaExternalLinkAlt style={{ fontSize: '0.75rem' }} /> Live Demo
                        </ProjectBtn>
                      )}

                      <ProjectBtn
                        href={project.githubLink || 'https://github.com/NimishKushwaha'}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FaGithub /> GitHub
                      </ProjectBtn>

                      <ViewMoreLink to={`/projects/${project.id}`}>
                        Details <FaArrowRight style={{ fontSize: '0.75rem' }} />
                      </ViewMoreLink>
                    </ProjectButtonsRow>
                  </ProjectBody>
                </ProjectCard>
              ))}
            </ProjectsGrid>

            <AllProjectsBtnWrapper>
              <ViewAllProjectsBtn to="/projects">
                View All Projects <FaArrowRight />
              </ViewAllProjectsBtn>
            </AllProjectsBtnWrapper>
          </Container>
        </ProjectsSection>
      </ScrollReveal>

      {/* 6. TIMELINE: EXPERIENCE & EDUCATION (NEW) */}
      <ScrollReveal>
        <TimelineSection>
          <Container>
            <SectionHeader>
              <SectionTag>journey</SectionTag>
              <SectionTitle>Experience &amp; Education</SectionTitle>
              <SectionSubtitle>
                Key milestones in academic foundation, internships, and technical leadership roles.
              </SectionSubtitle>
            </SectionHeader>

            <TimelineWrapper>
              <TimelineItem $alignRight={false}>
                <TimelineDot>
                  <FaGraduationCap />
                </TimelineDot>
                <TimelineCard>
                  <div className="date">2022 - 2026</div>
                  <h3>B.Tech in Computer Science &amp; Engineering</h3>
                  <h4>Amrita Vishwa Vidyapeetham, Chennai</h4>
                  <p>
                    Specializing in Full Stack Systems, Machine Learning, and Distributed Computing. Active contributor to technical clubs, hackathons, and published IEEE research.
                  </p>
                </TimelineCard>
              </TimelineItem>

              <TimelineItem $alignRight={true}>
                <TimelineDot>
                  <FaBriefcase />
                </TimelineDot>
                <TimelineCard>
                  <div className="date">2024</div>
                  <h3>Full Stack Developer Intern</h3>
                  <h4>Corizo</h4>
                  <p>
                    Designed and deployed scalable web applications, enhanced data integrity via MongoDB schema redesigns, and streamlined collaborative git workflows across remote developer teams.
                  </p>
                </TimelineCard>
              </TimelineItem>

              <TimelineItem $alignRight={false}>
                <TimelineDot>
                  <FaAward />
                </TimelineDot>
                <TimelineCard>
                  <div className="date">2024</div>
                  <h3>Grand Finalist</h3>
                  <h4>Smart India Hackathon (Software Edition)</h4>
                  <p>
                    Selected among thousands of nationwide participants to compete in the SIH Grand Finale, architecting low-bandwidth offline educational software for rural classrooms.
                  </p>
                </TimelineCard>
              </TimelineItem>

              <TimelineItem $alignRight={true}>
                <TimelineDot>
                  <FaLaptopCode />
                </TimelineDot>
                <TimelineCard>
                  <div className="date">2023 - Present</div>
                  <h3>Lead Developer &amp; Technical Coordinator</h3>
                  <h4>Mahi-Eco Club</h4>
                  <p>
                    Directed website development and technical logistics for campus environmental hackathons and sustainability campaigns, boosting digital student engagement.
                  </p>
                </TimelineCard>
              </TimelineItem>
            </TimelineWrapper>
          </Container>
        </TimelineSection>
      </ScrollReveal>

      {/* 7. RESEARCH PUBLICATIONS */}
      <ScrollReveal>
        <Publications publications={publications} />
      </ScrollReveal>

      {/* 8. GITHUB STATS SECTION (NEW) */}
      <ScrollReveal>
        <GitHubSection>
          <Container>
            <SectionHeader>
              <SectionTag>05. // activity</SectionTag>
              <SectionTitle>Open Source &amp; GitHub Stats</SectionTitle>
              <SectionSubtitle>
                Real-time activity and contributions across repositories and technical projects.
              </SectionSubtitle>
            </SectionHeader>

            <GitHubGrid>
              <GitHubCard>
                <img
                  src="https://github-readme-stats.vercel.app/api?username=NimishKushwaha&show_icons=true&theme=tokyonight&hide_border=true&bg_color=0f172a&title_color=38bdf8&text_color=cbd5e1&icon_color=06b6d4"
                  alt="Nimish Kushwaha's GitHub Stats"
                  loading="lazy"
                />
              </GitHubCard>

              <GitHubCard>
                <img
                  src="https://github-readme-stats.vercel.app/api/top-langs/?username=NimishKushwaha&layout=compact&theme=tokyonight&hide_border=true&bg_color=0f172a&title_color=38bdf8&text_color=cbd5e1"
                  alt="Nimish Kushwaha's Top Languages"
                  loading="lazy"
                />
              </GitHubCard>
            </GitHubGrid>

            <GitHubCtaWrapper>
              <GitHubCtaBtn
                href="https://github.com/NimishKushwaha"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaGithub /> Follow on GitHub (@NimishKushwaha)
              </GitHubCtaBtn>
            </GitHubCtaWrapper>
          </Container>
        </GitHubSection>
      </ScrollReveal>

      {/* 9. CALL-TO-ACTION BAND (NEW) */}
      <CtaBand>
        <Container>
          <CtaTitle>Let's build something exceptional together.</CtaTitle>
          <CtaSubtitle>
            Whether you have an ambitious web or mobile application, a research challenge in Federated Learning, or an engineering role open—let's connect!
          </CtaSubtitle>
          <CtaBtn href="#contact">
            Get in Touch <FaArrowRight />
          </CtaBtn>
        </Container>
      </CtaBand>

      {/* 10. CONTACT SECTION */}
      <ScrollReveal>
        <Contact />
      </ScrollReveal>

      {/* 11. FOOTER */}
      <Footer />

      {/* GALLERY IMAGE MODAL */}
      {selectedImage && (
        <ResumeModalOverlay onClick={() => setSelectedImage(null)}>
          <GalleryModalImage
            src={selectedImage}
            alt="Enlarged gallery view"
            onClick={(e) => e.stopPropagation()}
          />
        </ResumeModalOverlay>
      )}

      {/* RESUME VIEWER MODAL */}
      {isResumeModalOpen && (
        <ResumeModalOverlay onClick={() => setIsResumeModalOpen(false)}>
          <ResumeModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Nimish Kushwaha - Resume</h3>
              <ModalActions>
                <ModalActionLink
                  href="/Nimish_Kushwaha_Resume.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open in new tab"
                >
                  Open in Tab
                </ModalActionLink>
                <ModalActionLink
                  href="/Nimish_Kushwaha_Resume.pdf"
                  download="Nimish_Kushwaha_Resume.pdf"
                  title="Download Resume"
                >
                  <FaDownload /> Download
                </ModalActionLink>
                <CloseModalButton
                  onClick={() => setIsResumeModalOpen(false)}
                  title="Close Modal"
                >
                  <FaTimes />
                </CloseModalButton>
              </ModalActions>
            </ModalHeader>
            <ResumeIframe
              src="/Nimish_Kushwaha_Resume.pdf#view=FitH"
              title="Nimish Kushwaha Resume"
            />
          </ResumeModalContent>
        </ResumeModalOverlay>
      )}
    </MainWrapper>
  );
}
